import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowUpFromBracket, faFile, faSparkles, faWarning } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Progress from "@radix-ui/react-progress";
import * as Sentry from "@sentry/browser";
import cx from "classnames";

import { getNextBillingCycleAnchorDate } from "~/billing/utils";
import { MeetingSelect } from "~/common";
import type { MeetingEvent } from "~/common/MeetingSelect";
import { useID } from "~/lib/hooks";
import humanizeBytes from "~/lib/humanizeBytes";
import { warning } from "~/lib/prompts";
import { commitMutationPromise, useMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { THREE_PER_EM_SPACE } from "~/lib/unicode";
import { getFirstUserFacingError, nGetText } from "~/lib/utils";
import ChannelMultiSelector from "~/meetingRecorder/ChannelMultiSelector";
import { getAllLanguageOptionsAvailableForTranscription } from "~/meetingRecorder/languageUtils";
import { environment } from "~/models";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { Button, toast, Tooltip, useModals } from "~/ui";
import { SimpleInput } from "~/ui/Input";
import { HelpCenterIcon } from "~/ui/legacyFellowIndex";
import Modal from "~/ui/Modal";
import type { MultiSelectorOption } from "~/ui/selectors/AsyncMultiSelector";
import Selector, { type SelectorOption } from "~/ui/selectors/Selector";

import useIsUploadsEnabled from "./useIsUploadsEnabled";

import type { UploadModal_createMeetingRecordingFromUploadMutation as CreateMeetingRecordingFromUploadMutationType } from "~/__generated__/UploadModal_createMeetingRecordingFromUploadMutation.graphql";
import type { UploadModal_getUploadUrlMutation as GetUploadUrlMutationType } from "~/__generated__/UploadModal_getUploadUrlMutation.graphql";
import type { UploadModal_RemainingUploadCredits_account$key } from "~/__generated__/UploadModal_RemainingUploadCredits_account.graphql";
import type { UploadModalQuery } from "~/__generated__/UploadModalQuery.graphql";

import styles from "./UploadModal.module.css";

// TK: Support converting mp3/wav on the backend
const SUPPORTED_AUDIO_EXTENSIONS = ["m4a", "wav", "mp3"];
const SUPPORTED_VIDEO_EXTENSIONS = ["mp4"];
const SUPPORTED_UPLOAD_EXTENSIONS = [...SUPPORTED_AUDIO_EXTENSIONS, ...SUPPORTED_VIDEO_EXTENSIONS];
const SUPPORTED_UPLOAD_EXTENSIONS_READABLE = SUPPORTED_UPLOAD_EXTENSIONS.map(ext => ext.toUpperCase()).join(", ");

const MAX_UPLOAD_LENGTH_SECONDS = 60 * 60 * 3; // 3 hours

export function uploadFileToURL(
	file: File,
	uploadUrl: string,
	fields: Record<string, string>,
	setUploadProgress: (progress: number) => void,
): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("POST", uploadUrl, true);
		const formData = new FormData();
		for (const [key, value] of Object.entries(fields)) {
			formData.append(key, value);
		}
		formData.append("file", file);
		xhr.upload.addEventListener("progress", event => {
			if (event.lengthComputable) {
				setUploadProgress((event.loaded / event.total) * 100);
			}
		});
		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				setUploadProgress(100);
				resolve();
			} else {
				reject(new Error(`Failed to upload file: ${xhr.status} ${xhr.statusText}`));
			}
		});
		xhr.addEventListener("error", () => {
			reject(new Error("Failed to upload file"));
		});
		xhr.send(formData);
	});
}

async function getMediaDuration(file: File): Promise<number | null> {
	return new Promise<number | null>(resolve => {
		const media = document.createElement("video");
		const objectUrl = URL.createObjectURL(file);
		media.addEventListener("loadedmetadata", () => {
			URL.revokeObjectURL(objectUrl);
			resolve(media.duration);
		});

		media.addEventListener("error", () => {
			// If the browser can't load the file, it's probably not
			// one of our supported formats anyway.
			URL.revokeObjectURL(objectUrl);
			resolve(null);
		});

		media.src = objectUrl;
	});
}

/**
 * Check that the file is one of the supported file types, and that it's not
 * too large or too long, insofar as it's convenient to do on the front end.
 *
 * @param file - The file to validate.
 * @param maxAudioUploadBytes - The maximum size of an audio file.
 * @param maxVideoUploadBytes - The maximum size of a video file.
 * @returns An error message if the file is invalid, or null if it is valid.
 */
export async function validateDroppedFile(
	file: File,
	maxAudioUploadBytes: number,
	maxVideoUploadBytes: number,
): Promise<string | null> {
	const FILE_ERROR_MESSAGES = {
		unsupportedType: `File type is not supported. Try one of these formats: ${SUPPORTED_UPLOAD_EXTENSIONS_READABLE}`,
		videoTooLarge: `File is too large. Maximum size for videos is ${humanizeBytes(maxVideoUploadBytes)}`,
		audioTooLarge: `File is too large. Maximum size for audio files is ${humanizeBytes(maxAudioUploadBytes)}`,
		tooLong: `Recording is too long. Maximum length is ${MAX_UPLOAD_LENGTH_SECONDS / 60 / 60} hours`,
	};

	const extension = file.name.split(".").pop()?.toLowerCase();
	if (!extension) return FILE_ERROR_MESSAGES.unsupportedType;
	if (SUPPORTED_AUDIO_EXTENSIONS.includes(extension)) {
		if (file.size > maxAudioUploadBytes) return FILE_ERROR_MESSAGES.audioTooLarge;
	} else if (SUPPORTED_VIDEO_EXTENSIONS.includes(extension)) {
		if (file.size > maxVideoUploadBytes) return FILE_ERROR_MESSAGES.videoTooLarge;
	} else {
		return FILE_ERROR_MESSAGES.unsupportedType;
	}
	const length = await getMediaDuration(file);
	if (length == null) return FILE_ERROR_MESSAGES.unsupportedType;
	if (length > MAX_UPLOAD_LENGTH_SECONDS) return FILE_ERROR_MESSAGES.tooLong;
	return null;
}

export function FileDropArea({
	onFilePicked,
	expandToFullScreen,
	inUploadsPage,
	error,
	maxAudioUploadBytes: maxAudioSize,
	maxVideoUploadBytes: maxVideoSize,
}: {
	onFilePicked: (file: File) => void;
	expandToFullScreen: boolean;
	inUploadsPage: boolean;
	error: string | null;
	maxAudioUploadBytes: number;
	maxVideoUploadBytes: number;
}) {
	const id = useID();
	const [dragging, setDragging] = useState(false);
	const onFileInputChanged = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (!e.target.files?.length) return;
			const file = e.target.files[0];
			onFilePicked(file);
		},
		[onFilePicked],
	);
	const onFileDropped = useCallback(
		(e: React.DragEvent<HTMLLabelElement>) => {
			e.preventDefault();
			setDragging(false);
			if (!e.dataTransfer.files?.length) return;
			const file = e.dataTransfer.files[0];
			onFilePicked(file);
		},
		[onFilePicked],
	);
	const onDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setDragging(true);
	}, []);
	return (
		<>
			<label
				htmlFor={`${id}-file-input`}
				className={cx(styles.fileDropArea, {
					[styles.dragging]: dragging,
					[styles.expandToFullScreen]: expandToFullScreen,
					[styles.inUploadsPage]: inUploadsPage,
				})}
				onDrop={onFileDropped}
				onDragOver={onDragOver}
				onDragLeave={() => setDragging(false)}
			>
				{error && (
					<div className={styles.fileDropError}>
						<FontAwesomeIcon icon={faWarning} />
						{THREE_PER_EM_SPACE}
						{error}
					</div>
				)}
				<div className={styles.fileDropAreaInner}>
					<div className={styles.fileDropHeading}>
						<FontAwesomeIcon icon={faArrowUpFromBracket} style={{ fontSize: "0.75em", verticalAlign: "-0.05em" }} />
						&ensp;Drag &amp; drop file here or <span className={styles.fakeLink}>Choose a file</span>
					</div>
					<div className={styles.fileDropDetails}>Formats: {SUPPORTED_UPLOAD_EXTENSIONS_READABLE}</div>
					<div className={styles.fileDropDetails}>
						{maxAudioSize != maxVideoSize
							? `Maximum size: ${humanizeBytes(maxAudioSize)} Audio / ${humanizeBytes(maxVideoSize)} Video`
							: `Maximum size: ${humanizeBytes(maxAudioSize)}`}
					</div>
					{inUploadsPage && (
						<div className={styles.fileDropMessage}>
							<FontAwesomeIcon icon={faSparkles} style={{ fontSize: "0.75em", verticalAlign: "-0.05em" }} />
							{THREE_PER_EM_SPACE}Fellow will automatically transcribe and generate an AI Recap for your uploads.
						</div>
					)}
				</div>
			</label>
			<input
				id={`${id}-file-input`}
				accept={SUPPORTED_UPLOAD_EXTENSIONS.map(ext => `.${ext}`).join(",")}
				type="file"
				onChange={onFileInputChanged}
				className="sr-only"
				value=""
				// Providing `value` here means that selecting the same file
				// twice in a row will trigger the onChange event, because
				// the <input>'s value never actually changes.
			/>
		</>
	);
}

function FileIcon() {
	return (
		<div className={styles.fileIcon}>
			<FontAwesomeIcon icon={faFile} />
		</div>
	);
}

function UploadingFile({ file, uploadProgress }: { file: File; uploadProgress: number }) {
	return (
		<div className={styles.uploadingFile}>
			<div className={styles.fileDetails}>
				<FileIcon />
				<div>
					<div className={styles.fileName}>{file.name}</div>
					<div className={styles.fileMetadata}>{humanizeBytes(file.size)}</div>
				</div>
			</div>
			<div className={styles.progressArea}>
				<Progress.Root className={styles.progressBar} value={uploadProgress} max={100}>
					<Progress.Indicator
						className={styles.progressBarIndicator}
						style={{ transform: `translateX(-${100 - uploadProgress}%)` }}
					/>
				</Progress.Root>
				<span className={styles.progressText}>{Math.round(uploadProgress)}%</span>
			</div>
		</div>
	);
}

function UploadedFile({ file }: { file: File }) {
	return (
		<div className={styles.uploadedFile}>
			<div className={styles.fileDetails}>
				<FileIcon />
				<div>
					<div className={styles.fileName}>{file.name}</div>
					<div className={styles.fileMetadata}>{humanizeBytes(file.size)}</div>
				</div>
			</div>
		</div>
	);
}

function OptionalIndicator() {
	return <span className={styles.optionalIndicator}>(optional)</span>;
}

function RemainingUploadCredits({ account }: { account: UploadModal_RemainingUploadCredits_account$key }) {
	const { uploadCredits, billingAccount } = useFragment(
		graphql`
			fragment UploadModal_RemainingUploadCredits_account on Account {
				billingAccount {
					subscriptionState
					subscriptionBillingCycleAnchor
				}
				uploadCredits {
					includedUploadCredits
					includedCreditsUsedCurrentCycle
				}
			}
		`,
		account,
	);
	const subscriptionIsActive = billingAccount.subscriptionState === "SUBSCRIBED";
	const nextCycleAnchor = React.useMemo(() => {
		if (!subscriptionIsActive) return null;
		return getNextBillingCycleAnchorDate(billingAccount.subscriptionBillingCycleAnchor);
	}, [billingAccount.subscriptionBillingCycleAnchor, subscriptionIsActive]);

	const nextCycleAnchorRelative = React.useMemo(() => {
		if (!nextCycleAnchor || !subscriptionIsActive) return null;
		const now = new Date();
		const anchor = new Date(nextCycleAnchor);
		const diffTime = Math.abs(anchor.getTime() - now.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays <= 0 ? "today" : `in ${diffDays} ${nGetText(diffDays, "day")}`;
	}, [nextCycleAnchor, subscriptionIsActive]);

	const usedCreditsPercentage =
		(uploadCredits.includedCreditsUsedCurrentCycle / uploadCredits.includedUploadCredits) * 100;
	if (usedCreditsPercentage < 80) return null;

	return (
		<div className={styles.uploadCredits}>
			<Tooltip content={`Upload limit will reset ${nextCycleAnchorRelative}`} disabled={!subscriptionIsActive}>
				<FontAwesomeIcon icon={faInfoCircle} />
			</Tooltip>
			{THREE_PER_EM_SPACE}Uploads used this month: {uploadCredits.includedCreditsUsedCurrentCycle}/
			{uploadCredits.includedUploadCredits}
		</div>
	);
}

/** Returns a function that opens the upload modal
 *
 * @param viewerKey - The viewer from Relay, with a UploadModal_useUploadModal_viewer fragment
 * @param analyticsLocationDescription - The `ux_location` value of what triggered the upload modal to open
 */
export function useUploadModal(analyticsLocationDescription: string) {
	const uploadsEnabled = useIsUploadsEnabled();
	const modals = useModals();

	const openUploadModal = useCallback(
		(defaultFile: File | null, defaultChannels: ReadonlyArray<MultiSelectorOption> | null) => {
			if (!uploadsEnabled) throw new Error("Uploads are not enabled, this shouldn't be available");
			dispatchAnalyticsTrack("Upload Modal Opened", {
				// eslint-disable-next-line camelcase
				ux_location: analyticsLocationDescription,
			});
			modals.open(({ close }) => (
				<React.Suspense fallback={null}>
					<UploadModal close={close} defaultChannels={defaultChannels} defaultFile={defaultFile} />
				</React.Suspense>
			));
		},
		[analyticsLocationDescription, modals, uploadsEnabled],
	);

	return openUploadModal;
}

function cleanFilename(filename: string) {
	return filename.replace(/\.[^.]+$/, "");
}

export default function UploadModal({
	close,
	defaultChannels,
	defaultFile,
}: {
	close: (didUpload: boolean) => void;
	defaultChannels: ReadonlyArray<MultiSelectorOption> | null;
	defaultFile: File | null;
}) {
	const {
		viewer: { account, recordingsAndClips },
	} = useLazyLoadQuery<UploadModalQuery>(
		graphql`
			query UploadModalQuery {
				viewer {
					account {
						billingAccount {
							videoUploadSizeLimitBytes: usageLimitForFeature(feature: ai_upload_video_size_bytes)
							audioUploadSizeLimitBytes: usageLimitForFeature(feature: ai_upload_audio_size_bytes)
						}
						...UploadModal_RemainingUploadCredits_account
					}

					recordingsAndClips(first: 1, entityType: recordings) {
						edges {
							node {
								... on MeetingRecording {
									detectedLanguage
								}
							}
						}
					}
				}
			}
		`,
		{},
	);
	const lastUsedLanguage = recordingsAndClips.edges[0]?.node.detectedLanguage ?? null;
	const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [meetingTitle, setMeetingTitle] = useState<string>("");

	const [meetingLanguage, _setMeetingLanguage] = useState<string | null>(lastUsedLanguage);
	const setMeetingLanguage = useCallback((option: SelectorOption | null) => {
		_setMeetingLanguage(option?.value ?? null);
	}, []);

	const [selectedMeeting, _setSelectedMeeting] = useState<MeetingEvent | null>(null);
	const setSelectedMeeting = useCallback(
		(newValue: MeetingEvent | readonly MeetingEvent[] | null | undefined) => {
			const meeting = newValue && "length" in newValue ? newValue[0] : newValue;
			if (meeting && (!meetingTitle || (selectedFile != null && meetingTitle === cleanFilename(selectedFile.name))))
				setMeetingTitle(meeting?.title ?? "");
			_setSelectedMeeting(meeting ?? null);
		},
		[meetingTitle, selectedFile],
	);

	const [selectedChannels, setSelectedChannels] = useState<ReadonlyArray<MultiSelectorOption> | null>(defaultChannels);

	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const onFilePicked = useCallback(
		async (file: File) => {
			const validationError = await validateDroppedFile(
				file,
				account.billingAccount.audioUploadSizeLimitBytes ?? 0,
				account.billingAccount.videoUploadSizeLimitBytes ?? 0,
			);
			setUploadError(validationError);
			if (validationError) return;
			try {
				const fileExtension = `.${file.name.split(".").pop()}`;
				if (!fileExtension) throw new Error("File has no extension");
				const uploadAuthorizationResponse = await commitMutationPromise<GetUploadUrlMutationType>(environment, {
					mutation: graphql`
						mutation UploadModal_getUploadUrlMutation($fileExtension: String!) {
							getMeetingRecordingUploadUrl(fileExtension: $fileExtension) {
								uploadUrl
								fields
							}
						}
					`,
					variables: { fileExtension },
				});
				if (!uploadAuthorizationResponse.getMeetingRecordingUploadUrl) throw new Error("No upload URL");
				const uploadAuthorization = uploadAuthorizationResponse.getMeetingRecordingUploadUrl;
				const uploadFields = uploadAuthorization.fields;

				setSelectedFile(file);
				setMeetingTitle(prevName => prevName || cleanFilename(file.name));
				setUploadedFileKey(uploadFields["key"]);
				await uploadFileToURL(file, uploadAuthorization.uploadUrl, uploadFields, setUploadProgress);
			} catch (e) {
				const userFacingError = getFirstUserFacingError(e) ?? "Something went wrong, please try again later";
				toast.error(userFacingError, { title: "Upload failed" });
				setUploadedFileKey(null);
				setUploadProgress(null);
				setSelectedFile(null);
				if (e instanceof Error) {
					Sentry.captureException(e);
				}
				console.error(e);
			}
		},
		[account.billingAccount.audioUploadSizeLimitBytes, account.billingAccount.videoUploadSizeLimitBytes],
	);

	const cancel = useCallback(async () => {
		if (
			!selectedFile ||
			(await warning("Are you sure you want to cancel this upload? The selected file will be discarded.", {
				title: "Cancel upload?",
				yesText: "Yes",
				noText: "No",
			}))
		) {
			close(false);
		}
	}, [close, selectedFile]);

	useEffect(() => {
		if (defaultFile) {
			onFilePicked(defaultFile);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [createMeetingRecordingFromUpload, createMeetingRecordingFromUploadIsLoading] =
		useMutationPromise<CreateMeetingRecordingFromUploadMutationType>(graphql`
			mutation UploadModal_createMeetingRecordingFromUploadMutation(
				$title: String!
				$language: String!
				$uploadedFileKey: String!
				$addToMeeting: ID
				$addToChannels: [ID!]
			) {
				createMeetingRecordingFromUpload(
					title: $title
					language: $language
					uploadedFileKey: $uploadedFileKey
					addToMeeting: $addToMeeting
					addToChannels: $addToChannels
				) {
					meetingRecording {
						id
					}
					account {
						# This has the side effect of updating remaining upload credits
						# in the UI
						...UploadModal_RemainingUploadCredits_account
					}
				}
			}
		`);

	const id = useID();

	const submit = useCallback(async () => {
		if (!uploadedFileKey) {
			toast.error("Upload is not done");
			return;
		}
		if (!meetingTitle) {
			toast.error("Please enter a meeting title");
			return;
		}
		if (meetingTitle.length >= 255) {
			toast.error("Meeting title must be less than 255 characters");
			return;
		}
		if (!meetingLanguage) {
			toast.error("Please select what language the meeting was in");
			return;
		}

		let meetingRecordingId: string | null = null;
		try {
			const result = await createMeetingRecordingFromUpload({
				variables: {
					title: meetingTitle,
					language: meetingLanguage,
					uploadedFileKey,
					addToMeeting: selectedMeeting?.id,
					addToChannels: selectedChannels?.map(c => c.value),
				},
			});
			meetingRecordingId = result.createMeetingRecordingFromUpload?.meetingRecording.id ?? null;
			if (!meetingRecordingId) throw new Error("No meeting recording ID");
		} catch (errors) {
			const userFacingError = getFirstUserFacingError(errors) ?? "Something went wrong, please try again.";
			toast.error(userFacingError, { title: "Failed to submit" });
			return;
		}
		recordingsEventEmitter.emit("Recordings.refetch");
		const fileExtension = selectedFile?.name.split(".").pop();
		const fileType = SUPPORTED_AUDIO_EXTENSIONS.includes(fileExtension ?? "") ? "audio" : "video";
		dispatchAnalyticsTrack("Uploaded Recording", {
			/* eslint-disable camelcase */
			meeting_recording_id: meetingRecordingId,
			event_linked: !!selectedMeeting?.id,
			type: fileType,
			/* eslint-enable camelcase */
		});
		close(true);
	}, [
		close,
		createMeetingRecordingFromUpload,
		meetingLanguage,
		meetingTitle,
		selectedChannels,
		selectedFile?.name,
		selectedMeeting?.id,
		uploadedFileKey,
	]);

	const uploadIsDone = uploadedFileKey && uploadProgress === 100;

	const supportedLanguages = useMemo(() => getAllLanguageOptionsAvailableForTranscription(), []);

	return (
		<Modal onClose={cancel}>
			<Modal.Header noBottomBorder>
				<Modal.Title>Upload audio or video</Modal.Title>
				<Modal.HeaderActions onClose={cancel}>
					<HelpCenterIcon articleId="10697541" title="Learn more about uploading meeting recordings" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body>
				{uploadProgress === null || !selectedFile ? (
					<FileDropArea
						onFilePicked={onFilePicked}
						expandToFullScreen={false}
						inUploadsPage={false}
						error={uploadError}
						maxAudioUploadBytes={account.billingAccount.audioUploadSizeLimitBytes ?? 0}
						maxVideoUploadBytes={account.billingAccount.videoUploadSizeLimitBytes ?? 0}
					/>
				) : uploadProgress < 100 ? (
					<UploadingFile file={selectedFile} uploadProgress={uploadProgress} />
				) : (
					<UploadedFile file={selectedFile} />
				)}
				<div className={styles.field}>
					<label htmlFor={`${id}-meeting-title`}>Meeting title</label>
					<SimpleInput
						id={`${id}-meeting-title`}
						value={meetingTitle}
						onValueChange={setMeetingTitle}
						required
						placeholder="Add a meeting title"
						maxLength={254}
						name="upload_meeting_title"
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor={`${id}-language`}>Meeting language</label>
					<Selector
						id={`${id}-language`}
						options={supportedLanguages}
						value={supportedLanguages.find(({ value }) => value === meetingLanguage) || null}
						onChange={setMeetingLanguage}
						required
						placeholder="Select a language…"
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor={`${id}-meeting`}>
						Link upload to an existing meeting <OptionalIndicator />
					</label>
					<MeetingSelect
						menuPortalTarget={document.body}
						menuPlacement="auto"
						useFeedbackStyle={false}
						reverse
						isClearable
						value={selectedMeeting}
						onChange={setSelectedMeeting}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor={`${id}-channel`}>
						Add to channel <OptionalIndicator />
					</label>
					<ChannelMultiSelector
						value={selectedChannels}
						onChange={setSelectedChannels}
						condensed={false}
						placeholder="Select a channel…"
					/>
				</div>
			</Modal.Body>
			<Modal.Footer noTopBorder>
				<Modal.FooterSecondaryActions style={{ alignItems: "center" }}>
					<RemainingUploadCredits account={account} />
				</Modal.FooterSecondaryActions>
				<Button variant="secondary" onClick={cancel}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={submit}
					disabled={!uploadIsDone || createMeetingRecordingFromUploadIsLoading}
				>
					Submit
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
